/**
 * Backend API route paths (used with axios client; baseURL = AppConfig.apiBaseUrl).
 */
export const apiRoutes = {
    auth: {
        login: "/api/users/auth/login/",
        register: "/api/users/auth/register/",
        logout: "/api/users/auth/logout/",
        me: "/api/users/auth/me/",
        tokenRefresh: "/api/users/auth/token/refresh/",
        changePassword: "/api/users/auth/change-password/",
        passwordReset: "/api/users/auth/password-reset/",
        passwordResetConfirm: "/api/users/auth/password-reset/confirm/",
    },
    dashboard: {
        /** Relational rollup (Django ``dashboard`` app, ORM only). */
        summary: "/api/dashboard/summary/",
        /** Legacy Mongo-backed analytics (``/api/v1/analytics/``). Prefer ``summary`` for new UI. */
        analytics: "/api/v1/analytics/",
        /** Relational notifications app: ``App/urls.py`` → ``/api/notifications/``. */
        notifications: {
            list: "/api/notifications/",
            markRead: (id: string) => `/api/notifications/${id}/read/`,
            markUnread: (id: string) => `/api/notifications/${id}/unread/`,
            delete: (id: string) => `/api/notifications/${id}/`,
            markAllRead: "/api/notifications/mark-all-read/",
        },
        users: {
            // Backed by Django users app (UserModel), not Mongo.
            // Base path prefix from App/urls.py is /api/users/
            list: "/api/users/",
            detail: (id: string) => `/api/users/${id}/`,
            report: (id: string) => `/api/users/${id}/played-scenarios/`,
            changePassword: (id: string) => `/api/users/${id}/change-password/`,
            update: (id: string) => `/api/users/${id}/profile/`,
        },
        companies: {
            list: "/api/users/companies/",
        },
        scenarios: {
            corporate: {
                list: "/api/v2/scenarios/corporate",
                /**
                 * Corporate flags.
                 *
                 * Backend expects: /api/v2/scenarios/corporate/<scenario_type>/flags
                 * and currently only accepts scenario_type="corporate".
                 */
                flags: "/api/v2/scenarios/corporate/corporate/flags",
                flagsByScenarioType: (scenarioType: string) =>
                    `/api/v2/scenarios/corporate/${scenarioType}/flags`,
                flagCreate: "/api/v2/scenarios/corporate/flags",
                flagUpdate: (flagId: string) => `/api/v2/scenarios/corporate/flags/${flagId}`,
            },
            // Exercises list
            list: "/api/exercises",
            detail: (id: string) => `/api/exercises/${id}/detail`,
            /** PATCH prepare validation + approve (Django `ExerciseApproveAPIView`). */
            approval: (scenarioId: string) => `/api/exercises/${scenarioId}/approve`,
            ttx: (id: string) => `/api/exercises/${id}/ttx`,
            /** Same handlers as v1 `scenarios/<id>/injects`; mounted at `/api/` for a stable path. */
            injects: (scenarioId: string) => `/api/scenarios/${scenarioId}/injects`,
            injectDetail: (scenarioId: string, injectId: number) =>
                `/api/scenarios/${scenarioId}/injects/${injectId}`,
            start: (exerciseId: string) => `/api/exercises/${exerciseId}/start`,
            actives: {
                /** Relational exercise runs (Django exercise app; no Mongo). */
                list: "/api/scenarios/active",
                detail: (id: string) => `/api/v2/scenarios/active/${id}`,
                /** Relational exercise run console (VM); ``participant`` = user id. */
                console: (id: string) => `/api/exercises/active/${id}/console`,
                /** POST Nova SOFT reboot for the VM on that console. */
                softReboot: (id: string) => `/api/exercises/active/${id}/console/soft-reboot`,
                /** GET Nova power status without minting a new noVNC token. */
                vmStatus: (id: string) => `/api/exercises/active/${id}/console/status`,
                end: (id: string) => `/api/exercises/actives/${id}/end`,
                exerciseRun: (activeScenarioId: string) =>
                    `/api/v2/scenarios/active/${activeScenarioId}/exercise-run`,
                participants: (activeScenarioId: string) =>
                    `/api/v2/scenarios/active/${activeScenarioId}/exercise-run/participants`,
                participantRemove: (activeScenarioId: string, userId: string) =>
                    `/api/v2/scenarios/active/${activeScenarioId}/exercise-run/participants/${userId}`,
                usersSearch: (activeScenarioId: string) =>
                    `/api/v2/scenarios/active/${activeScenarioId}/exercise-run/users-search`,
                /** POST `ExerciseActiveInjectSendAPIView` — `exercise.urls_api`: `/api/exercises/actives/.../send`. */
                sendInject: (exerciseActiveId: string | number, injectId: number) =>
                    `/api/exercises/actives/${exerciseActiveId}/injects/${injectId}/send`,
                markRankInjectDelivery: (exerciseActiveId: string | number, deliveryId: number) =>
                    `/api/exercises/actives/${exerciseActiveId}/injects/deliveries/${deliveryId}/mark-rank`,
                evaluateInjectDelivery: (activeScenarioId: string, deliveryId: number) =>
                    `/api/v2/scenarios/active/${activeScenarioId}/exercise-run/deliveries/${deliveryId}/evaluate`,
            },
        },
        /** Injects app: list (GET) / create (POST). GET /api/injects/  POST /api/injects/ */
        injectsAll: "/api/injects/",
        /** Single inject: get, update, delete. GET/PATCH/DELETE /api/injects/<id>/ */
        injectDetail: (id: number) => `/api/injects/${id}/`,
        phases: {
            list: "/api/phases/",
            detail: (id: number) => `/api/phases/${id}`,
            exercisePhases: {
                listByExerciseId: (exerciseId: string) => `/api/exercises/${exerciseId}/phases`,
                detail: (exerciseId: string, id: number) => `/api/exercises/${exerciseId}/phases/${id}`,
            },
        },
        walkthroughs: {
            listByExerciseId: (exerciseId: string) => `/api/exercises/${exerciseId}/walkthroughs`,
            detail: (exerciseId: string, id: number) => `/api/exercises/${exerciseId}/walkthroughs/${id}`,
        },
        exerciseTeamMappings: {
            listByExerciseId: (exerciseId: string) => `/api/exercises/${exerciseId}/team-mappings`,
            detail: (exerciseId: string, id: number) => `/api/exercises/${exerciseId}/team-mappings/${id}`,
        },
        exerciseFlags: {
            listByExerciseId: (exerciseId: string) => `/api/exercises/${exerciseId}/flags`,
            detail: (exerciseId: string, id: number) => `/api/exercises/${exerciseId}/flags/${id}`,
        },
        /** Immutable relational exercise archives (Django ExerciseArchiveModel). */
        exerciseArchives: {
            listByExerciseId: (exerciseId: string) => `/api/exercises/${exerciseId}/archives`,
            listAll: "/api/exercises/archives",
            bulkReports: "/api/exercises/archives/reports/bulk",
            immutableReport: (archiveUuid: string) =>
                `/api/exercises/archives/${archiveUuid}/report`,
        },
        /**
         * Relational exercise runs (Django ExerciseActiveModel) — list + start without Mongo.
         */
        /** GET one run by stable uuid (same as list item id / active_id). */
        activeRunDetail: (activeId: string) => `/api/scenarios/active/${activeId}`,
        exerciseRuns: {
            listByExerciseId: (exerciseId: string) => `/api/exercises/${exerciseId}/runs`,
            create: (exerciseId: string) => `/api/exercises/${exerciseId}/runs`,
            detail: (exerciseId: string, runId: number) =>
                `/api/exercises/${exerciseId}/runs/${runId}`,
            start: (exerciseId: string, runId: number) =>
                // Launch (active-run) endpoint.
                `/api/exercises/actives/${runId}/launch`,
            /** POST: ``launched`` -> ``started`` (begin play for participants). */
            startScenario: (runId: number) => `/api/exercises/actives/${runId}/start-scenario`,
            finalizeParticipants: (runId: number) =>
                `/api/exercises/actives/${runId}/finalize-participantst`,
            participants: (exerciseId: string, runId: number) =>
                `/api/exercises/${exerciseId}/runs/${runId}/participants`,
            participantRemove: (exerciseId: string, runId: number, userId: string) =>
                `/api/exercises/${exerciseId}/runs/${runId}/participants/${userId}`,
            usersSearch: (exerciseId: string, runId: number) =>
                `/api/exercises/${exerciseId}/runs/${runId}/users-search`,
            participantRoleOptions: (exerciseId: string, runId: number) =>
                `/api/exercises/${exerciseId}/runs/${runId}/participant-role-options`,
        },
        flags: {
            list: "/api/flags/",
            detail: (id: string) => `/api/flags/${id}/`,
        },
        injectTypes: "/api/injects/types",
        roles: {
            // Legacy wrapper: we use teams semantics under /api/teams/
            list: "/api/teams/",
            detail: (id: number) => `/api/teams/${id}/`,
        },
    },
    users: {
        // Aliases for non-dashboard users endpoints; now point to Django users app.
        detail: (id: string) => `/api/users/${id}/`,
        reports: (id: string) => `/api/users/${id}/played-scenarios/`,
        changePassword: (id: string) => `/api/users/${id}/change-password/`,
        update: (id: string) => `/api/users/${id}/profile/`,
        adminUpdate: (id: string) => `/api/users/${id}/admin-update/`,
    },
    reports: {
        // v2 report detail (by report_id)
        detail: (id: string) => `/api/v2/reports/${id}`,
    },
    avatar: {
        list: "/api/avatar/list",
    }
} as const;

